import { useEffect, useState } from 'react';
import axios from 'axios';

export const MANUAL_PAYMENT_LEGACY_AUTH_ENABLED = ['1', 'true', 'yes', 'y'].includes(
  String(process.env.REACT_APP_MANUAL_PAYMENT_LEGACY_AUTH || '').trim().toLowerCase(),
);

function readEnv(name, fallback = '') {
  const value = process.env[name];
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function envBoolean(name) {
  return toBoolean(readEnv(name));
}

function normalizeMode(value, allowed, fallback) {
  const normalized = String(value || '').trim().toUpperCase();
  return allowed.includes(normalized) ? normalized : fallback;
}

export const MANUAL_PAYMENT_ENV_DEFAULTS = Object.freeze({
  pharmacySelectionMode: normalizeMode(readEnv('REACT_APP_TERMINAL_PHARMACY_SELECTION_MODE'), ['LEGACY', 'MAPPED'], 'LEGACY'),
  productNameMode: normalizeMode(readEnv('REACT_APP_TERMINAL_PRODUCT_NAME_MODE'), ['CLIENT_PROVIDED', 'CUSTOMER_CODE_NAME'], 'CLIENT_PROVIDED'),
  terminalPharmacySource: normalizeMode(readEnv('REACT_APP_TERMINAL_PHARMACY_SOURCE'), ['EXCEL', 'SHINSUNG', 'DISABLED'], 'DISABLED'),
  mappedPharmacyRequired: envBoolean('REACT_APP_TERMINAL_MAPPED_PHARMACY_REQUIRED') ?? false,
  productNameServerGenerated: envBoolean('REACT_APP_TERMINAL_PRODUCT_NAME_SERVER_GENERATED') ?? false,
  productNameMaxBytes: Number(readEnv('REACT_APP_TERMINAL_PRODUCT_NAME_MAX_BYTES', '255')) || 255,
  buyerEmailRequired: envBoolean('REACT_APP_MANUAL_PAYMENT_BUYER_EMAIL_REQUIRED'),
  buyerPhoneRequired: envBoolean('REACT_APP_MANUAL_PAYMENT_BUYER_PHONE_REQUIRED'),
  pharmacyCacheEnabled: envBoolean('REACT_APP_TERMINAL_PHARMACY_CACHE_ENABLED') ?? true,
  passwordChangeMode: normalizeMode(readEnv('REACT_APP_TERMINAL_PASSWORD_CHANGE_MODE'), ['NONE', 'NOTICE'], 'NONE'),
});

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toUpperCase();
    if (['Y', 'YES', 'TRUE', '1'].includes(normalized)) return true;
    if (['N', 'NO', 'FALSE', '0'].includes(normalized)) return false;
  }
  return undefined;
}

function isKgInicisProvider(provider) {
  const raw = String(provider || '').toUpperCase();
  const normalized = raw.replace(/[^A-Z0-9]/g, '');
  return normalized.includes('KGINICIS') || normalized === 'INICIS' || raw.includes('이니시스');
}

function getRows(data) {
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.pgMerchants)) return data.pgMerchants;
  if (Array.isArray(data?.pg_merchants)) return data.pg_merchants;
  if (Array.isArray(data)) return data;
  return [];
}

function parseManualPaymentConfig(responseBody) {
  const body = responseBody ?? {};
  const nestedData = body?.data;
  const data = nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)
    ? { ...body, ...nestedData }
    : (nestedData ?? body);
  const rows = getRows(data);
  const activeRow = rows.find((row) => (
    toBoolean(firstDefined(row?.isActive, row?.is_active, row?.active, row?.selected, row?.isSelected, row?.is_selected)) === true
  ));

  const activePgProvider = String(firstDefined(
    data?.activePgProvider,
    data?.active_pg_provider,
    data?.activeProvider,
    data?.active_provider,
    data?.pgProvider,
    data?.pg_provider,
    activeRow?.pgProvider,
    activeRow?.pg_provider,
    activeRow?.provider,
  ) || '').toUpperCase();

  const requirements = firstDefined(
    data?.requirements,
    data?.manualPaymentRequirements,
    data?.manual_payment_requirements,
    activeRow?.requirements,
    activeRow?.manualPaymentRequirements,
    activeRow?.manual_payment_requirements,
    {},
  );

  const explicitBuyerEmailRequired = toBoolean(firstDefined(
    requirements?.buyerEmailRequired,
    requirements?.buyer_email_required,
    data?.buyerEmailRequired,
    data?.buyer_email_required,
    activeRow?.buyerEmailRequired,
    activeRow?.buyer_email_required,
  ));

  const explicitBuyerPhoneRequired = toBoolean(firstDefined(
    requirements?.buyerPhoneRequired,
    requirements?.buyer_phone_required,
    data?.buyerPhoneRequired,
    data?.buyer_phone_required,
    activeRow?.buyerPhoneRequired,
    activeRow?.buyer_phone_required,
  ));

  return {
    activePgProvider,
    legacyAuthRequired: MANUAL_PAYMENT_LEGACY_AUTH_ENABLED && isKgInicisProvider(activePgProvider),
    buyerEmailRequired: explicitBuyerEmailRequired ?? MANUAL_PAYMENT_ENV_DEFAULTS.buyerEmailRequired ?? isKgInicisProvider(activePgProvider),
    buyerPhoneRequired: explicitBuyerPhoneRequired ?? MANUAL_PAYMENT_ENV_DEFAULTS.buyerPhoneRequired ?? false,
    pharmacySelectionMode: normalizeMode(firstDefined(
      data?.pharmacySelectionMode,
      data?.pharmacy_selection_mode,
      MANUAL_PAYMENT_ENV_DEFAULTS.pharmacySelectionMode,
    ), ['LEGACY', 'MAPPED'], 'LEGACY'),
    productNameMode: normalizeMode(firstDefined(
      data?.productNameMode,
      data?.product_name_mode,
      MANUAL_PAYMENT_ENV_DEFAULTS.productNameMode,
    ), ['CLIENT_PROVIDED', 'CUSTOMER_CODE_NAME'], 'CLIENT_PROVIDED'),
    terminalPharmacySource: normalizeMode(firstDefined(
      data?.terminalPharmacySource,
      data?.terminal_pharmacy_source,
      MANUAL_PAYMENT_ENV_DEFAULTS.terminalPharmacySource,
    ), ['EXCEL', 'SHINSUNG', 'DISABLED'], 'DISABLED'),
    mappedPharmacyRequired: toBoolean(firstDefined(
      data?.mappedPharmacyRequired,
      data?.mapped_pharmacy_required,
      MANUAL_PAYMENT_ENV_DEFAULTS.mappedPharmacyRequired,
    )) ?? false,
    productNameServerGenerated: toBoolean(firstDefined(
      data?.productNameServerGenerated,
      data?.product_name_server_generated,
      MANUAL_PAYMENT_ENV_DEFAULTS.productNameServerGenerated,
    )) ?? false,
    productNameMaxBytes: Number(firstDefined(
      data?.productNameMaxBytes,
      data?.product_name_max_bytes,
      MANUAL_PAYMENT_ENV_DEFAULTS.productNameMaxBytes,
    )) || 255,
    pharmacyCacheEnabled: MANUAL_PAYMENT_ENV_DEFAULTS.pharmacyCacheEnabled,
    passwordChangeMode: MANUAL_PAYMENT_ENV_DEFAULTS.passwordChangeMode,
  };
}

export function isValidEmail(value) {
  const email = String(value || '').trim();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function normalizePhone(value) {
  return String(value || '').replace(/\D/g, '');
}

export function isValidPhone(value) {
  const phone = normalizePhone(value);
  return phone.length >= 9 && phone.length <= 11;
}

export default function useManualPaymentConfig(domain) {
  const [config, setConfig] = useState({
    activePgProvider: '',
    legacyAuthRequired: false,
    buyerEmailRequired: MANUAL_PAYMENT_ENV_DEFAULTS.buyerEmailRequired ?? false,
    buyerPhoneRequired: MANUAL_PAYMENT_ENV_DEFAULTS.buyerPhoneRequired ?? false,
    pharmacySelectionMode: MANUAL_PAYMENT_ENV_DEFAULTS.pharmacySelectionMode,
    productNameMode: MANUAL_PAYMENT_ENV_DEFAULTS.productNameMode,
    terminalPharmacySource: MANUAL_PAYMENT_ENV_DEFAULTS.terminalPharmacySource,
    mappedPharmacyRequired: MANUAL_PAYMENT_ENV_DEFAULTS.mappedPharmacyRequired,
    productNameServerGenerated: MANUAL_PAYMENT_ENV_DEFAULTS.productNameServerGenerated,
    productNameMaxBytes: MANUAL_PAYMENT_ENV_DEFAULTS.productNameMaxBytes,
    pharmacyCacheEnabled: MANUAL_PAYMENT_ENV_DEFAULTS.pharmacyCacheEnabled,
    passwordChangeMode: MANUAL_PAYMENT_ENV_DEFAULTS.passwordChangeMode,
    loading: true,
    loaded: false,
    error: null,
  });

  useEffect(() => {
    if (!domain) return undefined;

    const baseUrl = process.env.REACT_APP_API || '';
    const path = domain === 'terminal'
      ? '/api/terminal/payment/manual/config'
      : '/api/cp/payment/manual/config';

    let cancelled = false;
    setConfig((prev) => ({ ...prev, loading: true, error: null }));

    axios.get(baseUrl + path, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        setConfig({
          ...parseManualPaymentConfig(data),
          loading: false,
          loaded: true,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setConfig({
          activePgProvider: '',
          legacyAuthRequired: false,
          buyerEmailRequired: MANUAL_PAYMENT_ENV_DEFAULTS.buyerEmailRequired ?? false,
          buyerPhoneRequired: MANUAL_PAYMENT_ENV_DEFAULTS.buyerPhoneRequired ?? false,
          pharmacySelectionMode: MANUAL_PAYMENT_ENV_DEFAULTS.pharmacySelectionMode,
          productNameMode: MANUAL_PAYMENT_ENV_DEFAULTS.productNameMode,
          terminalPharmacySource: MANUAL_PAYMENT_ENV_DEFAULTS.terminalPharmacySource,
          mappedPharmacyRequired: MANUAL_PAYMENT_ENV_DEFAULTS.mappedPharmacyRequired,
          productNameServerGenerated: MANUAL_PAYMENT_ENV_DEFAULTS.productNameServerGenerated,
          productNameMaxBytes: MANUAL_PAYMENT_ENV_DEFAULTS.productNameMaxBytes,
          pharmacyCacheEnabled: MANUAL_PAYMENT_ENV_DEFAULTS.pharmacyCacheEnabled,
          passwordChangeMode: MANUAL_PAYMENT_ENV_DEFAULTS.passwordChangeMode,
          loading: false,
          loaded: true,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [domain]);

  return config;
}
